import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/authStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState();
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    // Auth endpoints should never trigger token refresh or redirect on 401
    const requestUrl = originalRequest?.url || '';
    if (requestUrl.includes('/auth/')) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        await useAuthStore.getState().refreshAccessToken();
        const { accessToken } = useAuthStore.getState();
        if (originalRequest.headers && accessToken) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        return api(originalRequest);
      } catch {
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }
    
    return Promise.reject(error);
  }
);

// API helper functions
export const authApi = {
  requestMagicLink: (email: string, purpose: 'login' | 'verify_email' | 'reset_password' = 'login') =>
    api.post('/auth/magic-link', { email, purpose }),
  
  verifyMagicLink: (token: string) =>
    api.post('/auth/verify', { token }),

  verify2fa: (temp_token: string, code: string) =>
    api.post('/auth/2fa/verify', { temp_token, code }),
  
  refreshToken: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),
  
  logout: () =>
    api.post('/auth/logout'),
  
  getMe: () =>
    api.get('/auth/me'),
};

export const userApi = {
  getProfile: () =>
    api.get('/users/me'),
  
  updateProfile: (data: { full_name?: string; avatar_url?: string | null }) =>
    api.patch('/users/me', data),
  
  getSessions: () =>
    api.get('/users/me/sessions'),
  
  revokeSession: (sessionId: string) =>
    api.delete(`/users/me/sessions/${sessionId}`),
  
  revokeAllSessions: () =>
    api.delete('/users/me/sessions'),

  get2faStatus: () =>
    api.get('/users/me/2fa'),

  setup2fa: () =>
    api.post('/users/me/2fa/setup'),

  enable2fa: (data: { secret: string; code: string; backup_codes: string[] }) =>
    api.post('/users/me/2fa/enable', data),

  disable2fa: (code: string) =>
    api.post('/users/me/2fa/disable', { code }),
};

export const organizationApi = {
  list: () =>
    api.get('/organizations'),
  
  create: (data: { name: string; slug?: string }) =>
    api.post('/organizations', data),
  
  get: (id: string) =>
    api.get(`/organizations/${id}`),
  
  update: (id: string, data: { name?: string; logo_url?: string | null; settings?: any }) =>
    api.patch(`/organizations/${id}`, data),
  
  delete: (id: string) =>
    api.delete(`/organizations/${id}`),
  
  listMembers: (id: string, params?: { page?: number; limit?: number; status?: string; role?: string }) =>
    api.get(`/organizations/${id}/members`, { params }),
  
  inviteMember: (id: string, data: { email: string; role?: string }) =>
    api.post(`/organizations/${id}/members`, data),
  
  updateMember: (id: string, memberId: string, data: { role: string }) =>
    api.patch(`/organizations/${id}/members/${memberId}`, data),
  
  removeMember: (id: string, memberId: string) =>
    api.delete(`/organizations/${id}/members/${memberId}`),
};

export const documentApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    status?: string[];
    type?: string[];
    search?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }) =>
    api.get('/documents', { params }),
  
  create: (data: {
    title: string;
    description?: string;
    type: string;
    workflow_type: 'sequential' | 'parallel';
    expires_in_days?: number;
    settings?: any;
  }) =>
    api.post('/documents', data),
  
  completeUpload: (id: string, data: {
    file_name: string;
    file_size: number;
    file_hash: string;
    pages_count: number;
    r2_key: string;
  }) =>
    api.post(`/documents/${id}/complete-upload`, data),
  
  get: (id: string) =>
    api.get(`/documents/${id}`),
  
  update: (id: string, data: {
    title?: string;
    description?: string | null;
    status?: string;
    signing_date?: string | null;
    expires_in_days?: number | null;
    settings?: any;
  }) =>
    api.patch(`/documents/${id}`, data),
  
  download: (id: string) =>
    api.get(`/documents/${id}/download`, { responseType: 'blob' }),
  
  send: (id: string, data: {
    signers: Array<{
      email: string;
      full_name: string;
      role?: string;
      order?: number;
      signature_type?: string;
    }>;
    message?: string;
  }) =>
    api.post(`/documents/${id}/send`, data),
  
  cancel: (id: string, reason?: string) =>
    api.post(`/documents/${id}/cancel`, { reason }),
  
  delete: (id: string) =>
    api.delete(`/documents/${id}`),
};

export const signerApi = {
  get: (signerId: string) =>
    api.get(`/signers/${signerId}`),
  
  view: (signerId: string) =>
    api.post(`/signers/${signerId}/view`),
  
  sign: (signerId: string, data: {
    signature_type: 'draw' | 'image' | 'text' | 'digital';
    signature_data: any;
    signed_at?: string;
    position?: {
      page?: number;
      x?: number;
      y?: number;
      width?: number;
      height?: number;
    };
  }) =>
    api.post(`/signers/${signerId}/sign`, data),
  
  decline: (signerId: string, reason?: string) =>
    api.post(`/signers/${signerId}/decline`, { reason }),
  
  remind: (signerId: string) =>
    api.post(`/signers/${signerId}/remind`),
  
  reset: (signerId: string) =>
    api.post(`/signers/${signerId}/reset`),
};

export const signatureApi = {
  getUploadUrl: (data: {
    signer_id: string;
    document_id: string;
    filename: string;
    content_type: string;
  }) =>
    api.post('/signatures/upload-url', data),
  
  confirmUpload: (data: {
    signer_id: string;
    r2_key: string;
    file_hash: string;
    image_width: number;
    image_height: number;
  }) =>
    api.post('/signatures/confirm', data),
  
  saveDraw: (data: {
    signer_id: string;
    strokes: Array<{
      points: Array<{ x: number; y: number; pressure?: number; timestamp: number }>;
      color: string;
      width: number;
    }>;
  }) =>
    api.post('/signatures/draw', data),
  
  saveText: (data: {
    signer_id: string;
    text: string;
    font_family?: string;
    font_size?: number;
    color?: string;
  }) =>
    api.post('/signatures/text', data),
  
  clear: (signerId: string) =>
    api.delete(`/signatures/${signerId}`),
  
  getImage: (signerId: string) =>
    api.get(`/signatures/${signerId}/image`, { responseType: 'blob' }),
};

export const templateApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    type?: string;
    search?: string;
  }) =>
    api.get('/templates', { params }),
  
  create: (data: {
    name: string;
    description?: string;
    type: string;
    workflow_type: 'sequential' | 'parallel';
    signature_positions?: any[];
    is_public?: boolean;
  }) =>
    api.post('/templates', data),
  
  completeUpload: (id: string, data: {
    file_name: string;
    file_size: number;
    file_hash: string;
    pages_count: number;
    r2_key: string;
  }) =>
    api.post(`/templates/${id}/complete-upload`, data),
  
  get: (id: string) =>
    api.get(`/templates/${id}`),
  
  update: (id: string, data: {
    name?: string;
    description?: string | null;
    type?: string;
    workflow_type?: string;
    signature_positions?: any[];
    is_public?: boolean;
  }) =>
    api.patch(`/templates/${id}`, data),
  
  createDocument: (id: string, data: {
    title: string;
    description?: string;
    expires_in_days?: number;
  }) =>
    api.post(`/templates/${id}/create-document`, data),
  
  delete: (id: string) =>
    api.delete(`/templates/${id}`),
};

export const notificationApi = {
  list: (params?: { page?: number; limit?: number; unread?: boolean; type?: string }) =>
    api.get('/notifications', { params }),
  
  markRead: (id: string) =>
    api.patch(`/notifications/${id}/read`),
  
  markAllRead: () =>
    api.patch('/notifications/read-all'),
  
  delete: (id: string) =>
    api.delete(`/notifications/${id}`),
};

export const webhookApi = {
  list: (params?: { page?: number; limit?: number }) =>
    api.get('/webhooks', { params }),
  
  create: (data: { url: string; events: string[] }) =>
    api.post('/webhooks', data),
  
  get: (id: string) =>
    api.get(`/webhooks/${id}`),
  
  update: (id: string, data: { url?: string; events?: string[]; active?: boolean }) =>
    api.patch(`/webhooks/${id}`, data),
  
  regenerateSecret: (id: string) =>
    api.post(`/webhooks/${id}/regenerate-secret`),
  
  delete: (id: string) =>
    api.delete(`/webhooks/${id}`),
  
  getDeliveries: (id: string, params?: { page?: number; limit?: number }) =>
    api.get(`/webhooks/${id}/deliveries`, { params }),
};

export const uploadApi = {
  upload: (key: string, file: File, onProgress?: (progress: number) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    
    return api.post('/upload/multipart', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        }
      },
    });
  },
  
  directUpload: (key: string, file: File, onProgress?: (progress: number) => void) => {
    return api.put(`/upload/${key}`, file, {
      headers: { 'Content-Type': file.type },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        }
      },
    });
  },
};

export const healthApi = {
  check: () => api.get('/health'),
  ready: () => api.get('/health/ready'),
  live: () => api.get('/health/live'),
};